/* ============================================================
   CLIPBOARD — fondo líquido (WebGL, sin dependencias)
   Fragment shader con fbm + domain warping: cada nivel de ruido arrastra
   al siguiente, que es lo que produce flujo real en vez de una imagen
   girando. Al calcularse por píxel no hay capas apiladas, así que no hay
   bordes ni cruces que se lean como grano.

   El canvas se cuelga de <body>, NUNCA dentro de una .shopify-section:
   esas llevan `contain: layout style` y capturarían el position:fixed.
   Mismo patrón que el canvas de estrellas del tema (#clash-space-stars).

   Si no hay WebGL no pasa nada: el CSS deja un degradado estático de
   respaldo y esto simplemente no arranca.
   ============================================================ */
(function () {
  'use strict';

  var SPEED = 2.0; // ritmo del flujo
  var RENDER_SCALE = 0.62; // se rinde a menos resolución y se escala: es un degradado, no se nota, y baja mucho el costo
  var MAX_DPR = 1.4;

  var VERT = [
    'attribute vec2 a_pos;',
    'void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }'
  ].join('\n');

  var FRAG = [
    'precision highp float;',
    'uniform vec2 u_res;',
    'uniform float u_time;',

    'float hash(vec2 p) {',
    '  p = fract(p * vec2(123.34, 456.21));',
    '  p += dot(p, p + 45.32);',
    '  return fract(p.x * p.y);',
    '}',

    'float noise(vec2 p) {',
    '  vec2 i = floor(p);',
    '  vec2 f = fract(p);',
    '  vec2 u = f * f * (3.0 - 2.0 * f);',
    '  float a = hash(i);',
    '  float b = hash(i + vec2(1.0, 0.0));',
    '  float c = hash(i + vec2(0.0, 1.0));',
    '  float d = hash(i + vec2(1.0, 1.0));',
    '  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);',
    '}',

    'float fbm(vec2 p) {',
    '  mat2 rot = mat2(0.80, 0.60, -0.60, 0.80);',
    '  float v = 0.0;',
    '  float a = 0.5;',
    '  for (int i = 0; i < 3; i++) {',
    '    v += a * noise(p);',
    '    p = rot * p * 2.0;',
    '    a *= 0.45;',
    '  }',
    '  return v;',
    '}',

    'void main() {',
    '  vec2 uv = (gl_FragCoord.xy - 0.5 * u_res) / min(u_res.x, u_res.y);',
    '  float t = u_time;',
    '  vec2 p = uv * 1.45;',

    // domain warping: q deforma el dominio de r, y r el de f
    '  vec2 q = vec2(fbm(p + t * 0.11), fbm(p + vec2(5.2, 1.3) - t * 0.09));',
    '  vec2 r = vec2(fbm(p + 1.5 * q + vec2(1.7, 9.2) + t * 0.15),',
    '                fbm(p + 1.5 * q + vec2(8.3, 2.8) - t * 0.13));',
    '  float f = fbm(p + 1.7 * r);',

    // curva dura: masas sólidas de gris, no un lavado uniforme
    '  float shade = smoothstep(0.20, 0.68, f);',
    '  vec3 col = mix(vec3(0.075), vec3(0.44), shade);',

    // reflejo direccional: el brillo de líquido
    '  float spec = pow(clamp(dot(normalize(r + 0.0001), vec2(0.6, 0.8)), 0.0, 1.0), 4.0);',
    '  col += spec * 0.05;',

    // veta oscura donde el warp se comprime: da profundidad
    '  col -= 0.05 * smoothstep(0.55, 1.0, length(q));',

    // viñeta
    '  float vig = 1.0 - smoothstep(0.45, 1.25, length(uv * vec2(1.0, 1.15)));',
    '  col *= mix(0.35, 1.0, vig);',

    // dither de 1/255: mata el banding de 8 bits sin verse como grano
    '  col += (hash(gl_FragCoord.xy) - 0.5) / 255.0;',

    '  gl_FragColor = vec4(max(col, 0.0), 1.0);',
    '}'
  ].join('\n');

  function compile(gl, type, src) {
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  function start() {
    if (!document.querySelector('.clipboard-page')) return;
    if (document.getElementById('clipboard-liquid-bg')) return;

    var canvas = document.createElement('canvas');
    canvas.id = 'clipboard-liquid-bg';
    canvas.setAttribute('aria-hidden', 'true');

    var gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: 'low-power'
    }) || canvas.getContext('experimental-webgl');
    if (!gl) return;

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;

    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var uRes = gl.getUniformLocation(prog, 'u_res');
    var uTime = gl.getUniformLocation(prog, 'u_time');

    document.body.appendChild(canvas);
    document.documentElement.classList.add('clipboard-liquid-on');

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var raf = null;
    var t0 = performance.now();

    // Se llama en cada frame: sale enseguida si nada cambió, y así se repara
    // sola si al arrancar el viewport todavía medía 0 (sección inyectada
    // oculta en el editor de temas, pestaña sin componer, etc.).
    function resize() {
      var vw = window.innerWidth || document.documentElement.clientWidth || 0;
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      if (vw < 2 || vh < 2) return;
      var dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR) * RENDER_SCALE;
      var w = Math.max(1, Math.round(vw * dpr));
      var h = Math.max(1, Math.round(vh * dpr));
      if (canvas.width === w && canvas.height === h) return;
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
      gl.uniform2f(uRes, w, h);
    }

    function frame(now) {
      resize();
      gl.uniform1f(uTime, ((now - t0) / 1000) * SPEED);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf = requestAnimationFrame(frame);
    }

    function play() {
      if (raf || reduce) return;
      t0 = performance.now() - 1000; // arranca con el flujo ya formado, no desde el frame 0
      raf = requestAnimationFrame(frame);
    }

    function pause() {
      if (!raf) return;
      cancelAnimationFrame(raf);
      raf = null;
    }

    resize();
    window.addEventListener('resize', resize, { passive: true });

    if (reduce) {
      // Un solo frame, quieto. Se repinta si el viewport cambia de tamaño.
      var paintOnce = function () {
        resize();
        gl.uniform1f(uTime, 4.0);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      };
      paintOnce();
      window.addEventListener('resize', paintOnce, { passive: true });
      return;
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) pause();
      else play();
    });

    // Con la página scrolleada lejos del fondo visible igual se ve, así que
    // no lo paramos por scroll: sólo por pestaña oculta.
    play();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
